import { createRoot, Root } from 'react-dom/client';
import { CurrentUserProvider, QualioQFrontEndProvider, ToastProvider } from '@qualio/ui-components';
import { ThemeProvider } from 'styled-components';
import ErrorBoundary from './views/ErrorBoundary';
import { theme } from './theme';
import AppContainer from './container';
import App from './App';
import { initDatadog } from './external/datadog';
import { CurrentUser } from '@qualio/ui-components/lib/types/CurrentUser';
import { CreateLaunchDarklyProvider } from './external/LaunchDarklyAdapter';
import { QeFeQueryClientProvider } from './queryClient';
import './i18n/config';
import './zodConfig';
import { prodValpackCompanyId, stagingValpackCompanyId } from './types/common/constants';

const DATADOG_EXCLUDED_COMPANIES = [stagingValpackCompanyId, prodValpackCompanyId];

// Store roots for cleanup in MFE architecture
const roots = new Map<string, Root>();

(window as any).renderQualityEvents = async (containerId: string, currentUser: CurrentUser) => {
  const container = document.getElementById(containerId);
  if (!container) {
    console.error(`Container #${containerId} not found`);
    return;
  }

  const shouldInitDatadog =
    process.env.REACT_APP_ENVIRONMENT !== 'development' && !DATADOG_EXCLUDED_COMPANIES.includes(currentUser.companyId);

  if (shouldInitDatadog) {
    initDatadog();
  }

  const LDProvider = await CreateLaunchDarklyProvider(currentUser);

  const root = createRoot(container);
  roots.set(containerId, root);

  root.render(
    <ErrorBoundary>
      <LDProvider>
        <QeFeQueryClientProvider>
          <AppContainer containerId={containerId}>
            <QualioQFrontEndProvider user={currentUser}>
              <ThemeProvider theme={theme}>
                <ToastProvider>
                  <CurrentUserProvider user={currentUser}>
                    <App />
                  </CurrentUserProvider>
                </ToastProvider>
              </ThemeProvider>
            </QualioQFrontEndProvider>
          </AppContainer>
        </QeFeQueryClientProvider>
      </LDProvider>
    </ErrorBoundary>,
  );
};

(window as any).unmountQualityEvents = (containerId: string) => {
  try {
    console.log('Attempting to unmount container', containerId);
    const root = roots.get(containerId);
    if (root) {
      root.unmount();
      roots.delete(containerId);
    }
  } catch (e) {
    console.log('did not unmount component: ', e);
    // even with the if statements its possible we have a race condition that is the if states the element exists BUT
    // then before we remove it the Application container has removed it
  }
};

if (process.env.REACT_APP_MOCK_SERVER === 'true') {
  console.log('Using mock servers');
  import('./mocks');
}
